import type {
  DailyTaskTeam,
  DailyTaskTeamCreatePayload,
} from "@sgdf/ui-library";
import type {
  DailyTaskTeamIn,
  DailyTaskTeamPatch,
} from "@sgdf/client-v2";
import {
  createDailyTaskTeam,
  destroyDailyTaskTeam,
  listDailyTaskTeams,
  patchDailyTaskTeam,
  retrieveDailyTaskTeam,
  setDailyTaskTeamParticipants,
} from "@sgdf/client-v2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { unref } from "vue";

import { apiCall } from "@/core/utils/api";
import { useStaleParticipantsErrorHandler } from "./participant";
import { useDailyTaskTeamId, useMeetingId } from "./routes";
import { usePermission } from "./permission";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getInvalidatePredicate = (meetingId: string) => (query: any) => {
  return (
    query.queryKey[0] === "meetings" &&
    query.queryKey[1] === meetingId &&
    typeof query.queryKey[2] === "string" &&
    ["daily-task-teams", "participants"].includes(query.queryKey[2])
  );
};

export function useDailyTaskTeam() {
  const meetingId = useMeetingId();
  const dailyTaskTeamId = useDailyTaskTeamId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "daily-task-teams", dailyTaskTeamId],
    queryFn: () =>
      apiCall(
        retrieveDailyTaskTeam({
          path: {
            meeting_id: unref(meetingId)!,
            team_id: unref(dailyTaskTeamId)!,
          },
        }),
      ),
    enabled: () => {
      const id = unref(meetingId);
      return (
        id != null &&
        unref(dailyTaskTeamId) != null &&
        hasPermission.value("view_dailytaskteam", id)
      );
    },
  });
}

export function useCreateDailyTaskTeamMutation() {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options: { team: DailyTaskTeamCreatePayload }) => {
      const body: DailyTaskTeamIn = {
        label: options.team.label,
        color: options.team.color ?? "#00395C",
      };
      return apiCall(
        createDailyTaskTeam({
          path: { meeting_id: unref(meetingId)! },
          body,
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["meetings", unref(meetingId), "daily-task-teams"],
      });
    },
  });
}

export function useUpdateDailyTaskTeamMutation() {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options: {
      team: Partial<DailyTaskTeam> & { id: string };
    }) => {
      const body: DailyTaskTeamPatch = {
        label: options.team.label,
        color: options.team.color,
      };
      return apiCall(
        patchDailyTaskTeam({
          path: { meeting_id: unref(meetingId)!, team_id: options.team.id },
          body,
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: getInvalidatePredicate(unref(meetingId)!),
      });
    },
  });
}

export function useDeleteDailyTaskTeamMutation() {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options: { id: string }) =>
      apiCall(
        destroyDailyTaskTeam({
          path: { meeting_id: unref(meetingId)!, team_id: options.id },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: getInvalidatePredicate(unref(meetingId)!),
      });
    },
  });
}

export const useDailyTaskTeams = () => {
  const meetingId = useMeetingId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "daily-task-teams"],
    queryFn: () =>
      apiCall(
        listDailyTaskTeams({ path: { meeting_id: unref(meetingId)! } }),
      ),
    enabled: () => {
      const id = unref(meetingId);
      return id != null && hasPermission.value("view_dailytaskteam", id);
    },
    staleTime: Infinity,
  });
};

export function useUpdateDailyTaskTeamParticipantMutation() {
  const meetingId = useMeetingId();
  const invalidatePredicate = getInvalidatePredicate(unref(meetingId)!);
  const onStaleParticipantsError =
    useStaleParticipantsErrorHandler(invalidatePredicate);

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (options: { teamId: string; participants: string[] }) =>
      apiCall(
        setDailyTaskTeamParticipants({
          path: { meeting_id: unref(meetingId)!, team_id: options.teamId },
          body: { participants: options.participants },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: invalidatePredicate });
    },
    onError: onStaleParticipantsError,
  });
}
